import type { FC } from "react";
import React from "react";

import type { RawParams } from "@uirouter/core";

import { colorsPalettes } from "@similarweb/styles";
import type { INavItemBadgeType, ISecondaryItem, IGroupSecondaryLevel } from "@similarweb/ui-components";
import { SecondaryItem, ESecondaryItemType, GroupSecondaryLevel } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { navigateWithCategorySupport } from "app/pages/industry-analysis/routes/navigationInterceptor";
import { LEGACY_TO_V2_MAPPING } from "app/pages/industry-analysis/routes/routes";
import {
    extractCategoriesFromRouteParams,
    isIndustryAnalysisRoute,
} from "app/pages/industry-analysis/routes/routeUtils";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { resolveRouterTrackingId } from "single-spa/react-app/router/resolveRouterTrackingId";

import type { INavItem } from "./../../types";
import { isBeta, isNew } from "./../utils";

/**
 * category-aware navigation for Industry Analysis routes
 */
const sidebarNavigate = (stateName: string, params: RawParams = {}, options: any = {}) => {
    if (isIndustryAnalysisRoute(stateName)) {
        return navigateWithCategorySupport(stateName, params, options);
    }
    // For non-Industry Analysis routes, use standard navigation
    return proNavigator.go(stateName, params, options);
};

const isItemSelected = (item: INavItem, activeItemName: string, urlParams: any): boolean => {
    const currentActiveTab = urlParams?.selectedTab || urlParams?.tab;

    // Check for exact match first
    const exactMatch = item.tab
        ? activeItemName === item.state && currentActiveTab === item.tab && !item.disableSelectedMode
        : activeItemName === item.state && !item.disableSelectedMode;

    if (exactMatch) {
        return true;
    }

    // Generic V2 route highlighting
    // When user is on any V2 route, also highlight the corresponding legacy sidebar item
    if (item.state && !item.disableSelectedMode) {
        // Check if the active route is a V2 route that maps to this legacy sidebar item
        const v2RouteForThisItem = LEGACY_TO_V2_MAPPING[item.state as keyof typeof LEGACY_TO_V2_MAPPING];
        if (v2RouteForThisItem && activeItemName === v2RouteForThisItem) {
            return true;
        }
    }

    return false;
};

const resolveItemBadge = (item: INavItem): INavItemBadgeType => {
    if (isBeta(item)) {
        return "beta";
    }

    if (isNew(item)) {
        return "new";
    }
    return "none";
};

const isDescendantState = (navItems: INavItem[], currState: string): boolean => {
    if (!currState) {
        return false;
    }
    if (!navItems || !Array.isArray(navItems) || navItems.length === 0) {
        return false;
    }

    for (const item of navItems) {
        if (item.state === currState) {
            return true;
        }

        if ("subItems" in item) {
            if (isDescendantState(item.subItems, currState)) {
                return true;
            }
        }
    }

    return false;
};

type ITrackingProps = {
    depth: number;
    type: string;
    report: string;
    report_group: string | null;
    report_domain: string | null;
    analytics_domain: string | null;
    position_order: number;
    is_dropdown_list: boolean;
    is_locked: boolean;
    tag: INavItemBadgeType | string;
    eventName?: string;
};

export const sidebarTrackingDispatcher = ({
    eventName = "sidebar_interaction",
    ...restTrackingProps
}: ITrackingProps) => {
    allTrackers.dispatchTrackEvent("Sidebar Interaction", "click", eventName, restTrackingProps);
};

export const defaultGroupItemClickHandler = (props) => (isOpen: boolean) => {
    sidebarTrackingDispatcher({
        ...props,
        type: isOpen ? "dropdown_close" : "dropdown_open",
    });
};

const groupItemClickHandler = (item) => (isOpen: boolean) => {
    defaultGroupItemClickHandler(item)(isOpen);
    if (item.isLocked && item.onClickLocked) {
        item.onClickLocked();
    } else if (item.state) {
        void sidebarNavigate(item.state, proNavigator.getItemParams(item));
    }
};

export const createSideNavHierarchy = async (
    itemsConfig: INavItem[],
    selectedItemId: string,
    urlParams,
    onItemClick = groupItemClickHandler,
): Promise<React.ReactNode[] | null> => {
    if (!itemsConfig || !Array.isArray(itemsConfig) || itemsConfig.length === 0) {
        return null;
    }

    let childItems = null;
    let reportItemCounter = 0;
    let groupItemCounter = 0;
    const elements = [];
    for (const item of itemsConfig) {
        if (item.hidden) {
            continue;
        }

        childItems = [];
        if (item.subItems && Array.isArray(item.subItems) && item.subItems.length > 0) {
            for (const subItem of item.subItems) {
                if (subItem.hidden) {
                    continue;
                }
                if (item.itemType !== ESecondaryItemType.Standalone) {
                    reportItemCounter++;
                }
                const reportBadgeType = subItem.BadgeType ?? resolveItemBadge(subItem);
                const isReportLocked = Boolean(subItem.isLocked && subItem.onClickLocked);
                const onReportItemClick = (positionOrder) => () => {
                    const reportState = proNavigator.getState(subItem.state);
                    const reportTrackingId = resolveRouterTrackingId(reportState);
                    const trackingReportProps = {
                        depth: 3,
                        type: "click",
                        report: reportTrackingId?.subSubSection,
                        report_group: translate(item.title),
                        report_domain: reportTrackingId?.subSection?.split("/")[0],
                        analytics_domain: reportTrackingId?.section,
                        position_order: positionOrder,
                        is_dropdown_list: false,
                        is_locked: subItem.lockIcon,
                        tag: reportBadgeType,
                    };
                    sidebarTrackingDispatcher(trackingReportProps);
                    if (isReportLocked) {
                        subItem.onClickLocked?.();
                    }
                };

                let subItemLink = null;
                if (subItem.state && !isReportLocked) {
                    const itemParams = proNavigator.getItemParams(subItem);

                    if (isIndustryAnalysisRoute(subItem.state) && itemParams.category) {
                        const categories = extractCategoriesFromRouteParams(itemParams);
                        if (categories.length > 0) {
                            const formattedParams = { ...itemParams, category: categories };
                            subItemLink = await proNavigator.hrefAsync(subItem.state, formattedParams);
                        } else {
                            subItemLink = await proNavigator.hrefAsync(subItem.state, itemParams);
                        }
                    } else {
                        subItemLink = await proNavigator.hrefAsync(subItem.state, itemParams);
                    }
                }

                const isDescendantSelected = isDescendantState(subItem.subItems, selectedItemId);
                childItems.push(
                    React.createElement(SecondaryItem, {
                        itemText: translate(subItem.title),
                        id: subItem.state,
                        isSelected: selectedItemId === subItem.state || isDescendantSelected,
                        itemType:
                            (subItem.lockIcon || isReportLocked) && !subItem.upsellBadgeComponent
                                ? ESecondaryItemType.ReportUpsell
                                : ESecondaryItemType.Report,
                        navBadgeType: reportBadgeType,
                        href: subItemLink,
                        onItemClick: onReportItemClick(reportItemCounter),
                        iconColor: colorsPalettes.indigo[300],
                        badgeComponent: subItem.upsellBadgeComponent,
                    }),
                );
            }
        }

        const itemSelected = isItemSelected(item, selectedItemId, urlParams);

        const componentToRender: FC<IGroupSecondaryLevel | ISecondaryItem> = item.itemComponent ?? GroupSecondaryLevel;
        const isDescendantOfGroupSelected = isDescendantState(item.subItems, selectedItemId);

        groupItemCounter++;
        const groupStateOrNearest = proNavigator.getState(item.state || item.subItems[0].state);
        const groupTrackingId = resolveRouterTrackingId(groupStateOrNearest);
        const groupBadgeType = item.BadgeType ?? resolveItemBadge(item);
        const trackingGroupProps = {
            depth: 2,
            type: "click",
            report: null,
            report_group: translate(item.title),
            report_domain: groupTrackingId?.subSection?.split("/")[0],
            analytics_domain: groupTrackingId?.section,
            position_order: groupItemCounter,
            is_dropdown_list: true,
            is_locked: false,
            tag: groupBadgeType,
        };
        const itemType =
            item.itemType === ESecondaryItemType.Standalone &&
            item.lockIcon &&
            item.itemComponentProps?.upsellIconName !== null
                ? ESecondaryItemType.StandaloneUpsell
                : item.itemType;

        // Generate href for standalone items
        let itemLink;
        if (item.itemType === ESecondaryItemType.Standalone && item.state && !item.isLocked) {
            const itemParams = proNavigator.getItemParams(item);
            itemLink = await proNavigator.hrefAsync(item.state, itemParams);
        }

        elements.push(
            React.createElement(
                componentToRender,
                {
                    id: item.id ?? item.state ?? item.title ?? item.name,
                    key: `${item.id ?? item.state ?? item.name}-${item.title}`,
                    itemText: translate(item.title),
                    isSelected: itemSelected || isDescendantOfGroupSelected,
                    onItemClick: () => onItemClick(item)(true),
                    onGroupItemClick: onItemClick(trackingGroupProps),
                    navBadgeType: item.BadgeType ?? resolveItemBadge(item),
                    selectedItemId,
                    itemType,
                    href: itemLink,
                    ...item.itemComponentProps,
                },
                childItems,
            ),
        );
    }

    return elements;
};
